import { useEffect, useState } from 'react';
import {
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle2,
  Timer,
  Zap,
  Target,
  BarChart2
} from 'lucide-react';

const AnimatedNumber = ({ value, decimals = 0, suffix = '', prefix = '' }) => {
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (value === undefined || value === null) return;
    const duration = 800;
    const steps = 30;
    const stepDuration = duration / steps;
    const increment = value / steps;
    let current = 0;
    let step = 0;

    const timer = setInterval(() => {
      step++;
      current = Math.min(value, increment * step);
      setDisplay(current);
      if (step >= steps) {
        clearInterval(timer);
        setDisplay(value);
      }
    }, stepDuration);

    return () => clearInterval(timer);
  }, [value]);

  return (
    <span className="tabular-nums">
      {prefix}{typeof display === 'number' ? display.toFixed(decimals) : display}{suffix}
    </span>
  );
};

const KpiCard = ({ label, value, suffix = '', prefix = '', decimals = 0, trend, trendValue, icon: Icon, color, testId }) => {
  const trendUp = trend === 'up';
  const trendColor = trendUp ? 'text-[var(--nexus-success)]' : 'text-[var(--nexus-danger)]';
  const TrendIcon = trendUp ? TrendingUp : TrendingDown;

  return (
    <div
      data-testid={testId}
      className="relative p-3 rounded-xl bg-[var(--nexus-bg-elevated)] border border-[var(--nexus-border)] shadow-[0_0_0_1px_rgba(30,42,69,0.9),0_10px_30px_rgba(0,0,0,0.35)] overflow-hidden"
    >
      {/* Subtle top highlight */}
      <div className="absolute top-0 left-0 right-0 h-px" style={{ background: `linear-gradient(90deg, transparent, ${color}22, transparent)` }}></div>
      
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[10px] font-medium uppercase tracking-wider text-[var(--nexus-text-muted)] mb-1">{label}</p>
          <p className="font-heading text-2xl font-semibold tracking-tight animate-nexus-count-up" style={{ color }}>
            <AnimatedNumber value={value} decimals={decimals} suffix={suffix} prefix={prefix} />
          </p>
        </div>
        <div
          className="flex items-center justify-center w-8 h-8 rounded-lg"
          style={{ backgroundColor: `${color}15` }}
        >
          <Icon size={16} style={{ color }} />
        </div>
      </div>

      {trendValue !== undefined && (
        <div className={`flex items-center gap-1 mt-2 ${trendColor}`}>
          <TrendIcon size={12} />
          <span className="text-[10px] font-mono tabular-nums">{trendValue}</span>
        </div>
      )}
    </div>
  );
};

export const KpiCards = ({ kpis }) => {
  if (!kpis) return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {[...Array(4)].map((_, i) => (
        <div key={i} className="p-3 rounded-xl bg-[var(--nexus-bg-elevated)] border border-[var(--nexus-border)] animate-pulse h-24" />
      ))}
    </div>
  );

  return (
    <div data-testid="kpi-cards" className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <KpiCard
        testId="kpi-card-resolution-rate"
        label="Auto Resolution"
        value={kpis.autonomous_resolution_rate}
        suffix="%"
        decimals={1}
        trend="up"
        trendValue="+2.3% this week"
        icon={Target}
        color="var(--nexus-accent)"
      />
      <KpiCard
        testId="kpi-card-active-p1-p2"
        label="Active P1/P2"
        value={kpis.active_p1_p2}
        trend={kpis.active_p1_p2 > 3 ? 'down' : 'up'}
        trendValue={kpis.active_p1_p2 > 3 ? `${kpis.active_p1_p2} active` : 'Under control'}
        icon={AlertTriangle}
        color="var(--nexus-danger)"
      />
      <KpiCard
        testId="kpi-card-resolved-today"
        label="Resolved Today"
        value={kpis.tickets_resolved_today}
        trend="up"
        trendValue="+12 from yesterday"
        icon={CheckCircle2}
        color="var(--nexus-success)"
      />
      <KpiCard
        testId="kpi-card-sla-compliance"
        label="SLA Compliance"
        value={kpis.sla_compliance}
        suffix="%"
        decimals={1}
        trend="up"
        trendValue="+0.4% this month"
        icon={Timer}
        color="var(--nexus-primary-bright)"
      />
      <KpiCard
        testId="kpi-card-mttr"
        label="MTTR (P1)"
        value={kpis.mttr_minutes}
        suffix=" min"
        decimals={1}
        trend="up"
        trendValue="-3.2 min vs last week"
        icon={Zap}
        color="var(--nexus-warning)"
      />
      <KpiCard
        testId="kpi-card-open-tickets"
        label="Open Tickets"
        value={kpis.total_open_tickets}
        trend="down"
        trendValue={`${kpis.total_open_tickets} pending`}
        icon={BarChart2}
        color="var(--nexus-info)"
      />
      <KpiCard
        testId="kpi-card-agent-actions"
        label="Agent Actions/hr"
        value={kpis.agent_actions_last_hour}
        trend="up"
        trendValue="+47 from last hour"
        icon={Zap}
        color="var(--nexus-accent)"
      />
      <KpiCard
        testId="kpi-card-change-success"
        label="Change Success"
        value={kpis.change_success_rate}
        suffix="%"
        decimals={1}
        trend="up"
        trendValue="100% this sprint"
        icon={CheckCircle2}
        color="var(--nexus-success)"
      />
    </div>
  );
};
